import { Plus } from "lucide-react";
import { useMemo, useState } from "react";

import {
  useFeedHealthAll,
  useFeeds,
  useNextUpAll,
  useNowPlayingAll,
} from "../../api/hooks.js";
import { Button } from "../../design-system/button.js";
import { StateSurface } from "../../design-system/surfaces.js";
import { PageHeading } from "../shared/page-heading.js";
import { ConsoleCard } from "./console-card.js";
import {
  buildConsoleRows,
  orderConsoleRows,
  summariseConsole,
} from "./console-model.js";
import { CreateFeedDialog } from "./create-feed-dialog.js";

/**
 * Every feed at once.
 *
 * There is no current feed here and no way to select one: the console is the whole
 * set, and the set having one member is not a different screen. What each feed is
 * playing, what is next on it and what the product has established about it are read
 * per feed from that feed's own routes, so a feed that is unreachable is one row
 * saying so beside rows that are fine.
 */
export function ConsolePage() {
  const feeds = useFeeds(true);
  const [creating, setCreating] = useState(false);

  const list = useMemo(() => feeds.data ?? [], [feeds.data]);
  const feedIds = useMemo(() => list.map((feed) => feed.id), [list]);
  const live = useNowPlayingAll(feedIds);
  const health = useFeedHealthAll(feedIds);

  // Each feed's plan is re-read when that feed's airing track changes and at no
  // other time — the revision is what is on the wire.
  const plans = useNextUpAll(
    useMemo(
      () =>
        list.map((feed) => ({
          feedId: feed.id,
          revision: live[feed.id]?.data?.nowPlaying?.historyId ?? "",
        })),
      [list, live],
    ),
  );

  const rows = useMemo(
    () =>
      orderConsoleRows(
        buildConsoleRows(
          list.map((feed) => ({
            feed,
            live: live[feed.id]?.data ?? null,
            liveLoading: live[feed.id]?.loading ?? true,
            liveError: live[feed.id]?.error ?? null,
            health: health[feed.id]?.data ?? null,
            next: plans[feed.id]?.[0] ?? null,
          })),
        ),
      ),
    [health, list, live, plans],
  );

  const summary = summariseConsole(rows);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <PageHeading
            subtitle="Every feed, what's playing, and its status."
            title="Feeds"
          />
          {rows.length > 0 ? (
            <p className="mt-3 font-mono text-[0.6875rem] tracking-[0.04em] text-ink-3">
              {`${String(summary.total)} ${summary.total === 1 ? "feed" : "feeds"}`}
              {` · ${String(summary.onAir)} on air`}
              {summary.attention > 0
                ? ` · ${String(summary.attention)} needing attention`
                : ""}
              {summary.disabled > 0
                ? ` · ${String(summary.disabled)} disabled`
                : ""}
            </p>
          ) : null}
        </div>
        <Button
          onClick={() => {
            setCreating(true);
          }}
        >
          <Plus aria-hidden="true" />
          New feed
        </Button>
      </div>

      {feeds.loading ? (
        <StateSurface kind="loading" title="Reading every feed" />
      ) : feeds.error ? (
        <StateSurface
          action={
            <Button onClick={feeds.reload} size="small" variant="secondary">
              Try again
            </Button>
          }
          kind="error"
          title="The feeds could not be read"
        >
          {feeds.error}
        </StateSurface>
      ) : rows.length === 0 ? (
        <StateSurface
          action={
            <Button
              onClick={() => {
                setCreating(true);
              }}
              size="small"
            >
              <Plus aria-hidden="true" />
              New feed
            </Button>
          }
          kind="empty"
          title="No feeds yet"
        >
          Create a feed to start streaming.
        </StateSurface>
      ) : (
        <ul className="flex flex-col gap-3">
          {rows.map((row) => (
            <li key={row.feed.id}>
              <ConsoleCard onChanged={feeds.reload} row={row} />
            </li>
          ))}
        </ul>
      )}

      <CreateFeedDialog
        onClose={() => {
          setCreating(false);
        }}
        onCreated={feeds.reload}
        open={creating}
      />
    </div>
  );
}
